/**
 * Shared client-side gate for every file picker in the CMS.
 *
 * The server is the real authority (apps/server/src/config/multer.js), but a
 * round trip that ends in a generic "סוג קובץ לא נתמך" is a poor way to learn
 * that a .heic is not accepted — and on the multi-file forms one rejected file
 * fails the whole save, losing everything else the user typed. These checks
 * mirror the server's, and deliberately key off the *extension*: that is what
 * AzureStorageService derives the blob's Content-Type from, so it — not the
 * browser-supplied mimetype — is what the server actually enforces.
 *
 * Messages are user-facing Hebrew and name the offending file, because these
 * pickers accept several files at once and "סוג קובץ לא נתמך" on its own leaves
 * the user guessing which one to remove.
 */

/** react-dropzone's `accept` shape: mimetype → list of extensions. */
export type AcceptMap = Record<string, string[]>;

export type RejectionReason = 'type' | 'empty' | 'size';

export interface FileRejection {
  name: string;
  reason: RejectionReason;
  message: string;
}

interface ValidateOptions {
  /** Allowed types. Omit to skip the type check. */
  accept?: AcceptMap;
  /** Per-file cap in bytes. Omit to skip the size check. */
  maxSize?: number;
}

/** "1.4 MB" — used in both hints and error messages so the two agree. */
export const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes < 0) return '0 KB';
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
};

/** Every extension an AcceptMap allows, lowercased and de-duplicated. */
export const extensionsFrom = (accept: AcceptMap): string[] =>
  [...new Set(Object.values(accept).flat().map((ext) => ext.toLowerCase()))];

/** "PDF, JPG, PNG" — a hint line the user can read, not a mimetype dump. */
export const describeAccept = (accept: AcceptMap): string =>
  extensionsFrom(accept)
    .map((ext) => ext.replace(/^\./, '').toUpperCase())
    .join(', ');

const extensionOf = (fileName: string): string => {
  const dot = (fileName || '').lastIndexOf('.');
  return dot > 0 ? fileName.slice(dot).toLowerCase() : '';
};

/**
 * Check one file. Returns null when it is fine, or the single most useful
 * reason it is not — a file that is both the wrong type and too large is
 * reported as the wrong type, since fixing the size would not help.
 */
export const validateFile = (
  file: File,
  { accept, maxSize }: ValidateOptions
): FileRejection | null => {
  const name = file.name || 'קובץ';

  if (accept) {
    const ext = extensionOf(name);
    if (!ext || !extensionsFrom(accept).includes(ext)) {
      return {
        name,
        reason: 'type',
        message: ext
          ? `הקובץ "${name}" מסוג ${ext} אינו נתמך.`
          : `הקובץ "${name}" ללא סיומת מוכרת ואינו נתמך.`
      };
    }
  }

  // Empty files come from dropped folders and from a few mobile share sheets.
  // They upload "successfully" and then render as a broken tile, so stop them
  // here rather than let the user discover it later.
  if (file.size === 0) {
    return { name, reason: 'empty', message: `הקובץ "${name}" ריק ולא ניתן להעלות אותו.` };
  }

  if (typeof maxSize === 'number' && file.size > maxSize) {
    return {
      name,
      reason: 'size',
      message: `הקובץ "${name}" גדול מדי (${formatFileSize(file.size)}). הגודל המרבי הוא ${formatFileSize(maxSize)}.`
    };
  }

  return null;
};

/** Split a batch into the files worth keeping and the reasons for the rest. */
export const partitionFiles = (
  files: File[],
  options: ValidateOptions
): { accepted: File[]; rejections: FileRejection[] } => {
  const accepted: File[] = [];
  const rejections: FileRejection[] = [];
  files.forEach((file) => {
    const rejection = validateFile(file, options);
    if (rejection) rejections.push(rejection);
    else accepted.push(file);
  });
  return { accepted, rejections };
};
